import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, reaction } from "mobx";
import FormSection from "./FormSection";

class Form {
  isSelected = false;
  isSelectedForAnswers = false;
  lastUpdate = null;
  seenByCoach = false;
  constructor(
    id = uuidv4(),
    name = "",
    type = "",
    weekDay = 0,
    date = Date(),
    sections = [],
    coach = null,
    // clientsIds = [],
    client = null,
    originalFormId = "",
    blockId = ""
  ) {
    this.id = id;
    this.name = name;
    this.type = type;
    this.weekDay = weekDay;
    this.date = date;
    this.sections = sections;
    this.coach = coach;
    this.client = client;
    this.originalFormId = originalFormId;
    this.blockId = blockId;
    makeAutoObservable(this);
  }

  updateUserForm(formData) {
    console.log("updateUserForm");

    this.name = formData["name"] ?? "";
    this.date = formData["date"]?.toDate() ?? new Date();
    this.type = formData["type"] ?? "";
    this.weekDay = formData["week_day"] ?? 0;
    this.originalFormId = formData["original_form_id"] ?? "";
    this.blockId = formData["block_id"] ?? "";
    this.seenByCoach = formData["seen_by_coach"] ?? false;

    if (formData.sections) {
      /* ELIMINAR SECTIONS */
      const firestoreSectionIDs = new Set(formData.sections.map((s) => s.id));

      const localSectionIDs = new Set(
        this.sections.map((s) => s.id.toString())
      );
      const deletedSectionIDs = [...localSectionIDs].filter(
        (id) => !firestoreSectionIDs.has(id)
      );

      deletedSectionIDs.forEach((id) => {
        const index = this.sections.findIndex(
          (section) => section.id.toString().toLowerCase() === id.toLowerCase()
        );

        if (index !== -1) {
          this.sections.splice(index, 1); // Remove the section from the array using splice
        }
      });

      const firestoreSections = formData.sections;
      firestoreSections.forEach((sectionData) => {
        const internalSection = this.sections.find(
          (s) => s.id.toString().toLowerCase() === sectionData.id.toLowerCase()
        );

        if (internalSection) {
          internalSection.updateUserSection(sectionData);
        } else {
          this.fetchUserSection(sectionData);
        }
      });
    }
    if (formData.last_update) {
      this.lastUpdate = formData.last_update.toDate();
    }
  }

  fetchUserSection(sectionData) {
    try {
      const newSection = new FormSection(
        sectionData.id,
        sectionData.name,
        sectionData.order,
        [],
        this
      );

      if (sectionData.questions) {
        sectionData.questions.forEach((questionData) => {
          newSection.fetchUserQuestion(questionData);
        });
      }
      this.sections.push(newSection);
      return newSection;
    } catch (error) {
      console.error("Error creating section:", error);
    }
  }

  get numberOfQuestions() {
    let n = 0;
    this.sections.forEach((s) => {
      n += s.questions.length;
    });

    return n;
  }

  get getSeenByCoach() {
    // For forms created on or after 2025-12-04, use the stored seenByCoach flag
    // For older forms (before 2025-12-04) we assume they are seen by default
    try {
      const formDate = this.date ? new Date(this.date) : null;
      if (!formDate || isNaN(formDate.getTime())) return true;

      // Dec 4, 2025 at start of day
      const threshold = new Date(2025, 11, 4, 0, 0, 0, 0);

      // If form is from Dec 4 or later, return the actual seenByCoach value
      // If form is older than Dec 4, return true (assume seen)
      if (formDate >= threshold) {
        return this.seenByCoach;
      } else {
        return true;
      }
    } catch (e) {
      return true;
    }
  }

  setIsSelected(value) {
    this.isSelected = value;
  }

  setIsSelectedForAnswers(value) {
    this.isSelectedForAnswers = value;
  }

  get hasQuestions() {
    const hasQuestions = this.sections.some((section) =>
      section.questions.some((question) => question.question !== "")
    );
    return hasQuestions;
  }

  get numberOfQuestionsAsked() {
    const count = this.sections.reduce((total, section) => {
      return (
        total +
        section.questions.filter((question) => question.question !== "").length
      );
    }, 0);
    return count;
  }

  get hasAnswers() {
    const hasAnswers = this.sections.some((section) =>
      section.questions.some((question) => question.answer !== "")
    );
    return hasAnswers;
  }

  get numberOfQuestionsAnswered() {
    const count = this.sections.reduce((total, section) => {
      return (
        total +
        section.questions.filter((question) => question.answer !== "").length
      );
    }, 0);
    return count;
  }

  get blockName() {
    if (this.client) {
      const block = this.client.blocks.find((b) => b.id === this.blockId);
      if (block) {
        return block.name;
      } else {
        return "-";
      }
    } else {
      return "";
    }
  }

  typeLocalized(t) {
    if (this.type === "Inicial") {
      return t("initialFormType-string");
    } else if (this.type === "Semanal") {
      return t("weeklyFormType-string");
    } else {
      return t("blockFormType-string");
    }
  }

  uploadToCloud() {
    let sectionsArray = this.sections.map((section) => {
      let questionsArray = section.questions.map((question) => ({
        id: question.id.toString().toLowerCase(),
        question: question.question,
        type: question.type,
        answer: question.answer,
        order: question.order,
      }));

      return {
        id: section.id.toString().toLowerCase(),
        name: section.name,
        order: section.order,
        questions: questionsArray,
      };
    });

    if (this.coach) {
      return {
        id: this.id.toString().toLowerCase(),
        name: this.name,
        type: this.type,
        week_day: this.weekDay,
        sections: sectionsArray,
        last_edit: new Date(),
        number_of_clients: this.coach.clientsInForm(this).length,
      };
    } else {
      // return {
      //   id: this.id.toString().toLowerCase(),
      //   name: this.name,
      //   type: this.type,
      //   week_day: this.weekDay,
      //   date: this.date,
      //   original_form_id: this.originalFormId,
      //   sections: sectionsArray,
      //   block_id: this.id && this.id,
      // };
      return {
        id: this.id.toString().toLowerCase(),
        name: this.name,
        type: this.type,
        week_day: this.weekDay,
        date: this.date,
        original_form_id: this.originalFormId,
        sections: sectionsArray,
        ...(this.id ? { block_id: this.blockId } : {}), // Conditionally add block_id
      };
    }
  }

  uploadInitialToCloud() {
    let sectionsArray = this.sections.map((section) => {
      let questionsArray = section.questions.map((question) => ({
        id: uuidv4().toString().toLowerCase(),
        question: question.question,
        type: question.type,
        answer: question.answer,
        order: question.order,
      }));

      return {
        id: uuidv4().toString().toLowerCase(),
        name: section.name,
        order: section.order,
        questions: questionsArray,
      };
    });
    return {
      id: uuidv4().toString().toLowerCase(),
      name: this.name,
      type: this.type,
      sections: sectionsArray,
    };
  }

  updateFirestoreFormData(formDocData) {
    formDocData["date"] = this.date;
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }
  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
}
export default Form;
